import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const DRAWER_WIDTH = 240;

const LINKS = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/billing', label: 'Billing' },
  { to: '/signup-codes', label: 'Signup Codes' },
  { to: '/organizations', label: 'Organizations' },
  { to: '/email-logs', label: 'Email' },
  { to: '/ai-models', label: 'AI Models' },
  { to: '/system-settings', label: 'System Wide Settings' },
  { to: '/knowledge', label: 'Knowledge' },
  { to: '/legal-packs', label: 'Legal packs' },
  { to: '/jobs', label: 'Background jobs' },
  { to: '/zavvy-usage', label: 'Zavvy usage' },
  { to: '/zavvy-chat', label: 'Zavvy Chat' },
  { to: '/trust-center-access', label: 'Trust Center' },
];

const EnvBadge = () => (
  <Box
    sx={{
      fontSize: 12,
      fontWeight: 600,
      px: 1,
      py: 0.25,
      borderRadius: 1,
      bgcolor: '#EEF2FF',
      color: '#3730A3',
      whiteSpace: 'nowrap',
    }}
  >
    {process.env.REACT_APP_ENV_BADGE || 'trinitydev'}
  </Box>
);

const NavList = ({ onNavigate }) => (
  <List sx={{ px: 0.5, py: 1 }}>
    {LINKS.map((link) => (
      <ListItemButton
        key={link.to}
        component={NavLink}
        to={link.to}
        end
        onClick={onNavigate}
        sx={{
          minHeight: 44,
          mx: 1,
          mb: 0.5,
          borderRadius: 1,
          color: '#374151',
          '&.active': {
            bgcolor: '#EFF6FF',
            color: '#1D4ED8',
          },
        }}
      >
        <ListItemText
          primary={link.label}
          primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
        />
      </ListItemButton>
    ))}
  </List>
);

const TrinityShell = ({ onLogout, children }) => {
  const isDesktop = useMediaQuery('(min-width:900px)', { noSsr: true });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.title = 'Zavior - Admin Portal';
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
      <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #E5E7EB' }}>
        <Typography variant="subtitle2" sx={{ color: '#6B7280', fontWeight: 600 }}>
          Menu
        </Typography>
      </Box>
      <NavList onNavigate={isDesktop ? undefined : closeMobile} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {isDesktop ? (
        <Box
          component="nav"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid #E5E7EB',
            bgcolor: '#fff',
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100vh',
            overflowY: 'auto',
          }}
        >
          {drawer}
        </Box>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={closeMobile}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
        >
          {drawer}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#fff',
            minHeight: 56,
          }}
        >
          {!isDesktop ? (
            <IconButton
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box
            component="img"
            src="/assets/images/zaviorLogo.png"
            alt="Zavior"
            sx={{ height: 28, width: 'auto' }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
            Zavior - Admin Portal
          </Typography>
          <EnvBadge />
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={onLogout}>
            Log out
          </Button>
        </Box>
        <Box sx={{ p: 3, flex: 1 }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default TrinityShell;
